import { fetchClient } from "@/api/api";
import type {
	batchAddInput,
	batchAddOutput,
	batchUpdateInput,
	batchUpdateOutput,
	deleteMultipleProxiesInput,
	deleteMultipleProxiesOutput,
	deleteOneInput,
	deleteOneOutput,
	getAllProxiesInput,
	getAllProxiesOutput,
	getProxiesFacetsInput,
	getProxiesFacetsOutput,
	updateOneInput,
	updateOneOutput,
} from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { normalizeEmptyStringsToNull } from "@/utils/normalize";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { toast } from "@st-marketplace/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PROXY_SERVICE_KEYS } from "./keys";

const useGetProxies: CustomQueryHook<getAllProxiesOutput, getAllProxiesInput> =
	(payload, options) => {
		return useQuery({
			...options,
			queryKey: PROXY_SERVICE_KEYS.useGetProxies(payload),
			queryFn: async () => {
				const { data, error } = await fetchClient.GET(
					"/v1/marketplace/proxies/",
					{
						params: { query: payload?.query },
						throwOnError: true,
					},
				);
				if (error) throw error;
				return data;
			},
		});
	};

const useBatchUpdateProxies: CustomMutationHook<
	batchUpdateInput,
	batchUpdateOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: PROXY_SERVICE_KEYS.useBatchUpdateProxies(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/proxies/",
				{
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("proxy.batchUpdateDialog.successMessage"));
			queryClient.invalidateQueries({
				queryKey: PROXY_SERVICE_KEYS.useGetProxies(),
			});
		},
	});
};

const useDeleteMultipleProxies: CustomMutationHook<
	deleteMultipleProxiesInput,
	deleteMultipleProxiesOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: PROXY_SERVICE_KEYS.useDeleteMultipleProxies(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/proxies/delete-many/",
				{
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: PROXY_SERVICE_KEYS.useGetProxies(),
			});
		},
	});
};

const useDeleteSingleProxy: CustomMutationHook<
	deleteOneInput,
	deleteOneOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: PROXY_SERVICE_KEYS.useDeleteSingleProxy(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.DELETE(
				"/v1/marketplace/proxies/{proxy_id}",
				{
					params: { path: payload.path },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("proxy.deleteAction.successMessage", { value: 1 }));
			queryClient.invalidateQueries({
				queryKey: PROXY_SERVICE_KEYS.useGetProxies(),
			});
		},
	});
};

const useBatchAddProxies: CustomMutationHook<batchAddInput, batchAddOutput> = (
	options,
) => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: PROXY_SERVICE_KEYS.useBatchAddProxies(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/proxies/",
				{
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("proxy.new.formSuccessMessage"));
			queryClient.invalidateQueries({
				queryKey: PROXY_SERVICE_KEYS.useGetProxies(),
			});
		},
		...options,
	});
};

const useUpdateProxy: CustomMutationHook<updateOneInput, updateOneOutput> = (
	options,
) => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: PROXY_SERVICE_KEYS.useUpdateProxy(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/proxies/{proxy_id}",
				{
					params: { path: payload.path },
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("proxy.edit.formSuccessMessage"));
			queryClient.invalidateQueries({
				queryKey: PROXY_SERVICE_KEYS.useGetProxies(),
			});
		},
		...options,
	});
};

const useGetProxyFacets: CustomQueryHook<
	getProxiesFacetsOutput,
	getProxiesFacetsInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PROXY_SERVICE_KEYS.useGetProxyFacets(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/proxies/facets/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

export {
	useDeleteMultipleProxies,
	useBatchUpdateProxies,
	useGetProxies,
	useBatchAddProxies,
	useDeleteSingleProxy,
	useUpdateProxy,
	useGetProxyFacets,
};
